import React, { useState, useEffect, useCallback } from 'react';
import './LogsWhatsappData.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle, faClock, faBan, faTimesCircle, faSync, faFilePdf,
    faSearch, faChevronLeft, faChevronRight, faCircleInfo, faEye, faXmark
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { apiRaw } from '../../../api';

const PER_PAGE = 50;

// Metadatos de cada estado (icono FontAwesome + color) — sin emojis.
const ESTADOS = {
    enviada:   { label: 'Enviadas',   icon: faCheckCircle,  clase: 'estado-enviada' },
    pendiente: { label: 'Pendientes', icon: faClock,        clase: 'estado-pendiente' },
    expirada:  { label: 'Expiradas',  icon: faBan,          clase: 'estado-expirada' },
    error:     { label: 'Errores',    icon: faTimesCircle,  clase: 'estado-error' },
};

// Reconstrucción del mensaje tal como llega por WhatsApp. El texto real vive en la plantilla
// aprobada de Kibot; aquí se replica con los {{n}} sustituidos por los parámetros guardados.
// Si el texto no coincide 1:1 con la plantilla aprobada, solo hay que ajustar estos strings.
const PLANTILLAS = {
    cliente_recordatorio_cita_v1:   'Hola {{1}}, te recordamos tu cita el {{2}} con tu terapeuta {{3}} en el CESECO {{4}}.\nUbicación: {{5}}\nComprobante: {{6}}',
    terapeuta_recordatorio_cita_v1: 'Hola {{1}}, te recordamos tu cita el {{2}} con {{3}} en el CESECO {{4}}.\nUbicación: {{5}}\nComprobante: {{6}}',
    cliente_confirmacion_cita_v1:   'Tu cita ha sido agendada para el {{1}} con tu terapeuta {{2}} en el CESECO {{3}}.\nUbicación: {{4}}\nComprobante: {{5}}',
    terapeuta_confirmacion_cita_v1: 'Se te ha agendado una cita para el {{1}} con {{2}} en el CESECO {{3}}.\nUbicación: {{4}}\nComprobante: {{5}}',
    cancelacion_citas:              'Hola {{1}}, te informamos que tu cita del {{2}} ha sido cancelada.',
};

// Sustituye los {{1}}, {{2}}... por los valores del bodyArray (en orden).
const renderMensaje = (template, body) => {
    const plantilla = PLANTILLAS[template];
    const params = Array.isArray(body) ? body : [];
    if (!plantilla) {
        return params.length ? params.join('\n') : '(sin cuerpo)';
    }
    return plantilla.replace(/\{\{(\d+)\}\}/g, (_, n) => params[Number(n) - 1] ?? '');
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

export default function LogsWhatsappData() {
    const [filas, setFilas] = useState([]);
    const [resumen, setResumen] = useState({ enviada: 0, pendiente: 0, expirada: 0, error: 0 });
    const [totalRecords, setTotalRecords] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null);   // notificación mostrada en la vista previa

    const [filtros, setFiltros] = useState({
        estado: '', tipo: '', destinatario: '', desde: '', hasta: '', q: '',
    });

    const setFiltro = (campo, valor) => {
        setFiltros((f) => ({ ...f, [campo]: valor }));
        setPage(1);
    };

    // Click en un chip del resumen: alterna el filtro de estado.
    const toggleEstado = (estado) => {
        setFiltros((f) => ({ ...f, estado: f.estado === estado ? '' : estado }));
        setPage(1);
    };

    const cargar = useCallback((signal) => {
        setLoading(true);
        const params = new URLSearchParams({ page, per_page: PER_PAGE });
        Object.entries(filtros).forEach(([k, v]) => { if (v) params.append(k, v); });

        apiRaw(`/notificaciones?${params.toString()}`, { method: 'GET', signal })
            .then((r) => r.json())
            .then((data) => {
                setFilas(data.data || []);
                setResumen(data.resumen || { enviada: 0, pendiente: 0, expirada: 0, error: 0 });
                setTotalRecords(data.totalRecords || 0);
                setLastPage(data.lastPage || 1);
            })
            .catch((e) => { if (e.name !== 'AbortError') console.error('Error cargando logs:', e); })
            .finally(() => setLoading(false));
    }, [filtros, page]);

    // Debounce: al cambiar filtros o página, recarga tras 300ms (aborta la anterior).
    useEffect(() => {
        const ctrl = new AbortController();
        const t = setTimeout(() => cargar(ctrl.signal), 300);
        return () => { clearTimeout(t); ctrl.abort(); };
    }, [cargar]);

    return (
        <div className='logsWhatsapp'>
            <h2 className='logs-titulo'>
                <FontAwesomeIcon icon={faWhatsapp} /> Logs de WhatsApp
            </h2>

            {/* Resumen: chips por estado (clic para filtrar) */}
            <div className='logs-resumen'>
                {Object.entries(ESTADOS).map(([key, meta]) => (
                    <button
                        key={key}
                        className={`logs-chip ${meta.clase} ${filtros.estado === key ? 'chip-activo' : ''}`}
                        onClick={() => toggleEstado(key)}
                        title={`Filtrar por ${meta.label.toLowerCase()}`}
                    >
                        <FontAwesomeIcon icon={meta.icon} />
                        <span className='chip-num'>{resumen[key]}</span>
                        <span className='chip-label'>{meta.label}</span>
                    </button>
                ))}
            </div>

            {/* Filtros */}
            <div className='filtrosContain'>
                <div className='inputsColumn'>
                    <input
                        type='text'
                        value={filtros.q}
                        onChange={(e) => setFiltro('q', e.target.value)}
                        placeholder='Buscar cliente, terapeuta o teléfono'
                    />
                </div>
                <div className='inputsColumn'>
                    <select value={filtros.tipo} onChange={(e) => setFiltro('tipo', e.target.value)}>
                        <option value=''>Todos los tipos</option>
                        <option value='recordatorio'>Recordatorio</option>
                        <option value='confirmacion'>Confirmación</option>
                        <option value='cancelacion'>Cancelación</option>
                    </select>
                </div>
                <div className='inputsColumn'>
                    <select value={filtros.destinatario} onChange={(e) => setFiltro('destinatario', e.target.value)}>
                        <option value=''>Cliente y terapeuta</option>
                        <option value='cliente'>Solo cliente</option>
                        <option value='terapeuta'>Solo terapeuta</option>
                    </select>
                </div>
                <div className='inputsColumn'>
                    <select value={filtros.estado} onChange={(e) => setFiltro('estado', e.target.value)}>
                        <option value=''>Todos los estados</option>
                        <option value='enviada'>Enviadas</option>
                        <option value='pendiente'>Pendientes</option>
                        <option value='expirada'>Expiradas</option>
                        <option value='error'>Errores</option>
                    </select>
                </div>
                <div className='inputsColumn'>
                    <label className='logs-fecha-label'>Desde</label>
                    <input type='date' value={filtros.desde} onChange={(e) => setFiltro('desde', e.target.value)} />
                </div>
                <div className='inputsColumn'>
                    <label className='logs-fecha-label'>Hasta</label>
                    <input type='date' value={filtros.hasta} onChange={(e) => setFiltro('hasta', e.target.value)} />
                </div>
                <button className='reload' onClick={() => cargar()} title='Recargar'>
                    <FontAwesomeIcon icon={faSync} />
                </button>
            </div>

            {/* Leyenda de estados */}
            <p className='logs-leyenda'>
                <FontAwesomeIcon icon={faCircleInfo} />{' '}
                <b>Enviada</b>: salió por WhatsApp · <b>Pendiente</b>: espera su fecha ·{' '}
                <b>Expirada</b>: ya no se enviará (debajo del estado dice por qué: cita cancelada, reemplazado por un aviso nuevo, cita a menos de 24 horas o no salió) · <b>Error</b>: falló el envío.
                Las fechas están en hora de México.
            </p>

            {/* Tabla */}
            <div className='table-container'>
                <table className='table'>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Mensaje</th>
                            <th>Estado</th>
                            <th>Tipo</th>
                            <th>Para</th>
                            <th>Cliente</th>
                            <th>Terapeuta</th>
                            <th>Fecha cita</th>
                            <th>Sede</th>
                            <th>Programada</th>
                            <th>Enviada</th>
                            <th>PDF</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan='12' className='logs-vacio'><FontAwesomeIcon icon={faSync} spin /> Cargando…</td></tr>
                        ) : filas.length === 0 ? (
                            <tr><td colSpan='12' className='logs-vacio'>Sin notificaciones con estos filtros.</td></tr>
                        ) : (
                            filas.map((n) => {
                                const meta = ESTADOS[n.estado] || { icon: faCircleInfo, clase: '' };
                                return (
                                    <tr key={n.id}>
                                        <td className='logs-id'>{n.id}</td>
                                        <td>
                                            <button className='logs-ver-btn' onClick={() => setPreview(n)} title='Ver el mensaje tal como llegó'>
                                                <FontAwesomeIcon icon={faEye} /> Ver
                                            </button>
                                        </td>
                                        <td>
                                            <span className={`logs-badge ${meta.clase}`}>
                                                <FontAwesomeIcon icon={meta.icon} /> {cap(n.estado)}
                                            </span>
                                            {n.motivo && <div className='logs-motivo'>{n.motivo}</div>}
                                        </td>
                                        <td>{cap(n.tipo)}</td>
                                        <td>{cap(n.destinatario)}</td>
                                        <td>{n.cliente || '—'}</td>
                                        <td>{n.terapeuta || '—'}</td>
                                        <td>{n.fecha_cita || '—'}</td>
                                        <td>{n.sede || '—'}</td>
                                        <td>{n.programada || '—'}</td>
                                        <td>{n.enviada || '—'}</td>
                                        <td>
                                            {n.pdf_url ? (
                                                <a href={n.pdf_url} target='_blank' rel='noreferrer' title='Ver PDF de la cita'>
                                                    <FontAwesomeIcon icon={faFilePdf} />
                                                </a>
                                            ) : '—'}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            <div className='logs-paginacion'>
                <span className='logs-total'>{totalRecords} registro(s)</span>
                <div className='logs-pag-btns'>
                    <button disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <span>Página {page} de {lastPage}</span>
                    <button disabled={page >= lastPage || loading} onClick={() => setPage((p) => p + 1)}>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>

            {/* Vista previa del mensaje tal como llega por WhatsApp */}
            {preview && (
                <div className='logs-modal-overlay' onClick={() => setPreview(null)}>
                    <div className='logs-modal' onClick={(e) => e.stopPropagation()}>
                        <div className='logs-modal-head'>
                            <span><FontAwesomeIcon icon={faWhatsapp} /> Vista previa · registro #{preview.id}</span>
                            <button className='logs-modal-close' onClick={() => setPreview(null)} title='Cerrar'>
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>

                        <div className='logs-chat'>
                            <div className='logs-burbuja'>
                                {renderMensaje(preview.template, preview.body)
                                    .split('\n')
                                    .map((linea, i) => <div key={i}>{linea || ' '}</div>)}
                                {preview.enviada && <span className='logs-burbuja-hora'>{preview.enviada}</span>}
                            </div>
                        </div>

                        <div className='logs-modal-meta'>
                            <div><b>Para:</b> {cap(preview.destinatario)} · {preview.telefono}</div>
                            <div><b>Estado:</b> {cap(preview.estado)}{preview.motivo ? ` (${preview.motivo})` : ''} · <b>Plantilla:</b> {preview.template}</div>
                            <div><b>Programada:</b> {preview.programada || '—'} · <b>Enviada:</b> {preview.enviada || '—'}</div>
                            {preview.pdf_url && (
                                <div>
                                    <FontAwesomeIcon icon={faFilePdf} />{' '}
                                    <a href={preview.pdf_url} target='_blank' rel='noreferrer'>Ver comprobante (PDF)</a>
                                </div>
                            )}
                        </div>
                        <p className='logs-modal-nota'>
                            <FontAwesomeIcon icon={faCircleInfo} /> El texto se reconstruye con la plantilla + los
                            parámetros enviados; el estilo exacto lo define la plantilla aprobada en WhatsApp.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
